import { createAction, createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { addAlerts } from '@/pages/alerts/alertSlice';
import { addResources } from '@/pages/resources/resourceCardsSlice';
import { sosAlertListApi } from '@/services/alertServices';
import { sosResourceListApi } from '@/services/resourceServices';

// Action creators
export const resetFilters = createAction('filters/resetFilters');
export const clearFiltersOnPageChange = createAction(
  'filters/clearFiltersOnPageChange',
);
export const addFilters = createAction('filters/addFilters');
export const clearFilters = createAction('filters/clearFilters');
export const updatePageNumber = createAction('filters/updatePageNumber');
export const updateAlertType = createAction('filters/updateAlertType');
export const addResourceFilters = createAction('filters/addResourceFilters');
export const clearFiltersAlerts = createAction('filters/clearFiltersAlerts');
export const updateResourcePageNumber = createAction(
  'filters/updateResourcePageNumber',
);
export const clearResourceFilters = createAction(
  'filters/clearResourceFilters',
);
export const selectedTable = createAction('filters/selectedTable');

/**
 * Fetch and add alerts based on filters
 */
export const fetchAndAddAlerts = createAsyncThunk(
  'alerts/fetchAndAddAlerts',
  async (filter, { dispatch }) => {
    const { cardData } = await sosAlertListApi(filter);
    const data = cardData;
    const finalData = data;
    if (finalData && finalData.length > 0) {
      dispatch(addAlerts(finalData));
    } else {
      dispatch(addAlerts([]));
    }
  },
);

/**
 * Fetch and add resources based on filters
 */
export const fetchAndResources = createAsyncThunk(
  'resources/fetchAndAddResources',
  async (filter, { dispatch }) => {
    const response = await sosResourceListApi(filter);
    const data = response;
    const finalData = data;
    if (finalData && finalData.length > 0) {
      dispatch(addResources(finalData));
    } else {
      dispatch(addResources([]));
    }
  },
);

/**
 * Filter middleware - intercepts filter actions and triggers data fetching
 * @param {Object} store - Redux store
 * @return {Function} Middleware function
 */
export const filterMiddleware =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    if (addFilters.match(action) || clearFilters.match(action)) {
      next(action);
      const state = getState();
      const selectedFilters = state.filters.selectedFilters;
      dispatch(fetchAndAddAlerts(selectedFilters));
    } else if (
      addResourceFilters.match(action) ||
      clearResourceFilters.match(action) ||
      resetFilters.match(action)
    ) {
      next(action);
      const state = getState();
      const selectedFilters = state.filters.resourceSelectedFilters;
      dispatch(fetchAndResources(selectedFilters));
    }
    return next(action);
  };

/**
 * Filter slice - manages filter state for different pages
 */
const filterSlice = createSlice({
  name: 'filters',
  initialState: {
    selectedFilters: {
      page: 0,
      alertType: [],
      application: [],
      priority: [],
      resourceGroup: [],
      businessFunc: [],
      namespace: [],
    },
    resourceSelectedFilters: {
      page: 0,
      resourceNameString: '',
      application: [],
      resourceName: [],
      namespace: [],
      resourceGroup: [],
      allResources: 0,
    },
    selectedTable: 'sr_resource',
  },
  reducers: {
    resetFilters: (state) => {
      state.resourceSelectedFilters = {
        page: 0,
        resourceNameString: '',
        application: [],
        resourceName: [],
        namespace: [],
        resourceGroup: [],
        allResources: 0,
      };
    },
    clearFiltersOnPageChange: (state) => {
      state.selectedFilters = { page: 0, alertType: [] };
      state.selectedTable = 'sr_resource';
      state.resourceSelectedFilters = {
        page: 0,
        resourceNameString: '',
        application: [],
        resourceName: [],
        namespace: [],
        resourceGroup: [],
        allResources: 0,
      };
    },
    selectedTable: (state, action) => {
      const payload = action.payload;
      state.selectedTable = payload;
    },
    addFilters: (state, action) => {
      const payload = action.payload;
      state.selectedFilters[payload.key] = payload.value;
    },
    clearFilters: (state, action) => {
      const payload = action.payload;
      state.selectedFilters = payload;
    },
    updatePageNumber: (state, action) => {
      const pageNumber = action.payload;
      state.selectedFilters.page = pageNumber;
    },
    updateResourcePageNumber: (state, action) => {
      const pageNumber = action.payload;
      state.resourceSelectedFilters.page = pageNumber;
    },
    updateAlertType: (state, action) => {
      state.selectedFilters.alertType = action.payload;
    },
    addResourceFilters: (state, action) => {
      const { key, value } = action.payload;
      state.resourceSelectedFilters[key] = value;
    },
    clearFiltersAlerts: (state, action) => {
      const payload = action.payload;
      const curretState = state.selectedFilters;
      state.selectedFilters = { ...curretState, ...payload };
    },
    clearResourceFilters: (state, action) => {
      const payload = action.payload;
      state.resourceSelectedFilters = payload;
    },
  },
});

export default filterSlice.reducer;
